import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Button, Card, Chip, LinearProgress, Stack, styled, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { DropzoneOptions, ErrorCode, FileRejection, useDropzone } from "react-dropzone";
import DownloadingOutlinedIcon from "@mui/icons-material/DownloadingOutlined";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import { GetFileSize } from "../../../Utils/helpers";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {
  cacheService,
  dciService,
  fileService,
  ingestService,
  mediaService,
  uploadService,
  userService,
} from "../../../Core/Api/services";
import { AsperaUploadMediaFilesDto } from "../../../Core/Api/Types/db-file-infos";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { UserDataCustomType } from "../../../Core/Api/Types/user-data-custom-type";
import { UserDataStatus } from "../../../Core/Api/Types/user-data-status";
import { UserDataType } from "../../../Core/Api/Types/user-data-type";
import {
  AsperaUploadCardData,
  CreateUploadCard,
  DeleteUploadCardData,
  UpdateUploadCardStatus,
} from "../../../Redux/Reducers/redux-slice-aspera-upload";
import { useAppDispatch } from "../../../Redux/redux-store";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { RemoveUploadingObject } from "../../../Redux/Reducers/redux-slice-asset";
import { PlatformDocumentType } from "../../../Core/Api/Types/platform-document-type";
import { readFileAsBinaryString } from "../../../Core/Utils/functions";
import { useTranslation } from "react-i18next";
import { PlatformResourceType } from "../../../Core/Api/Types/platform-resource-type";

export type UploadCardProps = {
  title: string;
  name?: string;
  resolution?: string;
  ratio?: string;
  cover?: string;
  coverRounded?: boolean;
  coverPlaceholderIcon?: React.ReactNode;
  error?: boolean;
  small?: boolean;
  dropZoneOptions?: DropzoneOptions;
  objectId: number;
  storageId?: number;
  format: UploadCardFormatDto;
  recommendedRatio?: "16 / 9" | "4 / 3" | "1 / 1";
  userHasRightToDelete: boolean;
  alreadyUploadedFile?: AsperaUploadMediaFilesDto;
  cardIndex?: number;
  lockUpload?: boolean;
  uploadButtonInCard?: boolean;
  context: UploadContext;
  onDroppedFiles?: (files: File[]) => void;
  onSuccessUpload?: (response?: any, temporaryUploadingName?: string) => void;
  file?: File;
  fullPath?: string;
  disableButton?: boolean;
  temporaryUploadingName?: string;
  cancelTrigger?: number;
  timeout?: number;
  platformDocProps?: { description: string; fileType: PlatformDocumentType; version: string; documentId?: number };
  clear?: () => void;
  resourceType?: PlatformResourceType;
};

export enum UploadContext {
  MediaFile,
  ProfilePicture,
  SupportTicket,
  KdmFile,
  KdmCert,
  Http,
  LegacyImporter,
  CreatePlatformDocument,
  UpdatePlatformDocument,
  RequestMetadata,
  ThumbnailBase64,
  PlatformResource,
}

export interface UploadCardHandle {
  performUpload: (dataType?: UserDataType, dataStatus?: UserDataStatus, dataCustomType?: UserDataCustomType) => void;
  performRemove: () => void;
}
export type UploadCardRef = React.ElementRef<typeof UploadCard>;

type UploadCardStatus = AsperaUploadCardData["uploadCardStatus"];

const UploadCard = React.forwardRef<UploadCardHandle, UploadCardProps>((props, ref) => {
  const { t } = useTranslation();
  const {
    title,
    name = "drop-file-zone",
    resolution,
    ratio,
    cover,
    coverRounded = false,
    coverPlaceholderIcon,
    error = false,
    small = false,
    dropZoneOptions = {},
    lockUpload,
    uploadButtonInCard = false,
    objectId,
    storageId,
    format,
    recommendedRatio,
    userHasRightToDelete,
    alreadyUploadedFile,
    cardIndex,
    context,
    onDroppedFiles,
    onSuccessUpload,
    file,
    fullPath,
    disableButton,
    temporaryUploadingName = "",
    cancelTrigger,
    timeout = 0,
    clear,
    resourceType,
  } = props;

  // This is duplicate of isDragReject cause it will turn border to red color, we use it because isDragReject can't be used atm, we need MimeTypes, when we got it, remove this and all relative code
  const [isErrored, setIsErrored] = React.useState(error);

  const [uploadStatus, setUploadStatus] = React.useState<UploadCardStatus>(
    alreadyUploadedFile ? "uploadDone" : file ? "readyToUpload" : "noFile"
  );
  const [fileErrors, setFileErrors] = React.useState<string[]>([]);
  const [fileData, setFileData] = React.useState<{ id: number; file: File }>();
  const [uploadProgress, setUploadProgress] = React.useState<{ percent: number; size: string }>({
    percent: 0,
    size: "",
  });

  useEffect(() => {
    file && setFileData({ id: -500, file: file });
    file && updateStatus("readyToUpload");
  }, [file]);

  const enqueueSnackbar = useCustomSnackbar();
  const dispatch = useAppDispatch();
  const abortController = React.useRef(new AbortController());
  const user = cacheService.GetUserInfo();

  useEffect(() => {
    if (cancelTrigger && cancelTrigger > 1) onClickCancel();
  }, [cancelTrigger]);

  // #region functions
  // function used to easily update status of card, updates status in redux if index is given
  const updateStatus = React.useCallback(
    (status: UploadCardStatus) => {
      setUploadStatus(status);
      if (cardIndex !== undefined) {
        dispatch(UpdateUploadCardStatus({ uploadCardIndex: cardIndex, uploadCardStatus: status }));
      }
    },
    [cardIndex, file]
  );

  // callback for progress bar on upload
  const onUploadProgress = React.useCallback((progressEvent: any) => {
    const { loaded, total } = progressEvent;
    const percentValue = Math.floor((loaded * 100) / total);
    setUploadProgress({ percent: percentValue, size: GetFileSize(loaded) });
  }, []);

  // manages on drop event of file in card
  const onDrop = React.useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      // if no file or no rejection
      if (!acceptedFiles.length && !fileRejections.length) return;
      // if errors
      if (fileRejections.length > 0) {
        // get them
        const errors = fileRejections.flatMap((x) => x.errors);
        let newErrors: string[] = [];
        errors.forEach((error) => {
          switch (error.code) {
            case ErrorCode.FileInvalidType:
              if (format)
                newErrors.push(
                  t("field:errorMessage.fileInvalidType", { fileType: format.acceptsFiles["application/octet-stream"] })
                );
              break;
            case ErrorCode.FileTooLarge:
              if (dropZoneOptions.maxSize)
                newErrors.push(t("field:errorMessage.fileTooLarge", { maxSize: GetFileSize(dropZoneOptions.maxSize) }));
              break;
            case ErrorCode.FileTooSmall:
              if (dropZoneOptions.minSize)
                newErrors.push(t("field:errorMessage.fileTooSmall", { minSize: GetFileSize(dropZoneOptions.minSize) }));
              break;
            case ErrorCode.TooManyFiles:
              if (dropZoneOptions.maxFiles)
                newErrors.push(t("field:errorMessage.tooManyFiles", { maxFiles: dropZoneOptions.maxFiles }));
              break;
            default:
              newErrors.push(error.message);
          }
          // display them
          setIsErrored(true);
          setFileErrors(newErrors);
        });
        return;
      }
      // if not
      setFileErrors([]);
      setIsErrored(false);
      // set file data and upload status
      setFileData({ id: 0, file: acceptedFiles[0] });
      updateStatus("readyToUpload");

      // callback if needed
      onDroppedFiles && onDroppedFiles(acceptedFiles);
    },
    [format, dropZoneOptions]
  );

  // init dropzone with above function for onDropCallback
  const { getRootProps, getInputProps, open, /*isDragReject,*/ isDragActive } = useDropzone({
    accept: format.acceptsFiles,
    multiple: false,
    onDrop,
    ...dropZoneOptions,
  });

  // manages remove button
  const onClickRemove = React.useCallback(() => {
    setFileData(undefined);
    updateStatus("noFile");
    if (context == UploadContext.KdmCert && clear) clear();
  }, [context, props]);

  // function that handles upload
  const handleUpload = React.useCallback(
    (dataType?: UserDataType, dataStatus?: UserDataStatus, dataCustomType?: UserDataCustomType) => {
      // If we can't upload, stop here
      if (!fileData || uploadStatus !== "readyToUpload") return;

      // Else we set status and process upload
      updateStatus("uploadInProgress");
      setTimeout(async () => {
        switch (context) {
          case UploadContext.Http:
            uploadService
              .UploadBrowserFile(
                objectId,
                fullPath || "",
                fileData.file,
                abortController.current.signal,
                onUploadProgress
              )
              .then((response) => {
                // on response, depending of status :
                if (response.success === true) {
                  // upload succesful
                  fileData.id = response.fileId!;
                  setFileData(fileData);
                  updateStatus("uploadDone");
                  enqueueSnackbar(t("alert:success.fileUploaded"), "success");
                  if (onSuccessUpload) {
                    onSuccessUpload(response, temporaryUploadingName);
                  }
                } else {
                  // upload unsuccesful
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  enqueueSnackbar(response.message || t("alert:error.undefinedError"), "error");
                  dispatch(RemoveUploadingObject(temporaryUploadingName));
                  //if (onFailedUpload) onFailedUpload();
                }
              })
              .catch((response) => {
                // manages errors of upload
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                dispatch(RemoveUploadingObject(temporaryUploadingName));
                //if (onFailedUpload) onFailedUpload();
                // if we canceled we don't want to notify user
                if (response.code && response.code === "ERR_CANCELED") return;
                enqueueSnackbar(response, "error");
              });

            break;

          case UploadContext.MediaFile:
            mediaService
              .UploadMediaFile(
                objectId,
                [fileData.file],
                format.id,
                dataType,
                dataStatus,
                dataCustomType,
                abortController.current.signal,
                onUploadProgress
              )
              .then((response) => {
                // on response, depending of status :
                if (response.success === true) {
                  // upload succesful
                  fileData.id = response.fileId!;
                  setFileData(fileData);
                  updateStatus("uploadDone");
                  enqueueSnackbar(t("alert:success.fileUploaded"), "success");
                  onSuccessUpload && onSuccessUpload(response, temporaryUploadingName);
                } else {
                  // upload unsuccesful
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  enqueueSnackbar(response.message || t("alert:error.undefinedError"), "error");
                }
              })
              .catch((response) => {
                // manages errors of upload
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                // if we canceled we don't want to notify user
                if (response.code && response.code === "ERR_CANCELED") return;
                enqueueSnackbar(response, "error");
              });
            break;
          case UploadContext.ProfilePicture:
            userService
              .EditProfilePicture(fileData!.file, abortController.current.signal, onUploadProgress)
              .then((response) => {
                if (!response.success || response.user === null) {
                  enqueueSnackbar(t("alert:error.profilePictureUpdated"), "error");
                  return;
                }

                enqueueSnackbar(t("alert:success.profilePictureUpdated"), "success");
                cacheService.SetUser(response.user);
                setFileData(undefined);
                updateStatus("noFile");
                onSuccessUpload && onSuccessUpload(response, temporaryUploadingName);
              });
            break;
          case UploadContext.SupportTicket:
            break;
          case UploadContext.KdmFile:
            dciService.UploadDkdm(objectId, fileData.file!, undefined, onUploadProgress).then((response) => {
              if (!response.success) {
                enqueueSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "error");
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                return;
              }

              enqueueSnackbar(t("alert:success.dkdmUploaded"), "success");
              setFileData(undefined);
              updateStatus("noFile");
              onSuccessUpload && onSuccessUpload(response, temporaryUploadingName);
            });
            break;
          case UploadContext.KdmCert:
            break;
          case UploadContext.LegacyImporter:
            uploadService
              .UploadLegacyImporterFile(storageId!, fileData.file, abortController.current.signal, onUploadProgress)
              .then((response) => {
                // on response, depending of status :
                if (response.success === true) {
                  // upload succesful
                  fileData.id = response.fileId!;
                  setFileData(fileData);
                  updateStatus("uploadDone");
                  enqueueSnackbar(t("alert:success.fileUploaded"), "success");
                  if (onSuccessUpload) {
                    onSuccessUpload(response, temporaryUploadingName);
                  }
                } else {
                  // upload unsuccesful
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  enqueueSnackbar(response.message || t("alert:error.undefinedError"), "error");
                  dispatch(RemoveUploadingObject(temporaryUploadingName));
                  //if (onFailedUpload) onFailedUpload();
                }
              })
              .catch((response) => {
                // manages errors of upload
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                dispatch(RemoveUploadingObject(temporaryUploadingName));
                //if (onFailedUpload) onFailedUpload();
                // if we canceled we don't want to notify user
                if (response.code && response.code === "ERR_CANCELED") return;
                enqueueSnackbar(response, "error");
              });

            break;
          case UploadContext.CreatePlatformDocument:
            if (!props.platformDocProps) return;
            fileService
              .CreatePlatformDocument(
                { ...props.platformDocProps, file: fileData.file },
                abortController.current.signal,
                onUploadProgress
              )
              .then((response) => {
                // on response, depending of status :
                if (response.success === true) {
                  // upload succesful
                  fileData.id = response.objectId!;
                  setFileData(fileData);
                  updateStatus("uploadDone");
                  enqueueSnackbar(t("alert:success.fileUploaded"), "success");
                  onSuccessUpload && onSuccessUpload(response, temporaryUploadingName);
                } else {
                  // upload unsuccesful
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  enqueueSnackbar(response.message || t("alert:error.undefinedError"), "error");
                }
              })
              .catch((response) => {
                // manages errors of upload
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                dispatch(RemoveUploadingObject(temporaryUploadingName));
                //if (onFailedUpload) onFailedUpload();
                // if we canceled we don't want to notify user
                if (response.code && response.code === "ERR_CANCELED") return;
                enqueueSnackbar(response, "error");
              });
            break;
          case UploadContext.UpdatePlatformDocument:
            if (!props.platformDocProps?.documentId) return;
            fileService
              .UpdatePlatformDocument(
                props.platformDocProps.documentId,
                { ...props.platformDocProps, file: fileData.file },
                abortController.current.signal,
                onUploadProgress
              )
              .then((response) => {
                // on response, depending of status :
                if (response.success === true) {
                  // upload succesful
                  fileData.id = response.objectId!;
                  setFileData(fileData);
                  updateStatus("uploadDone");
                  enqueueSnackbar(t("alert:success.fileUploaded"), "success");
                  onSuccessUpload && onSuccessUpload(response, temporaryUploadingName);
                } else {
                  // upload unsuccesful
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  enqueueSnackbar(response.message || t("alert:error.undefinedError"), "error");
                }
              })
              .catch((response) => {
                // manages errors of upload
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                dispatch(RemoveUploadingObject(temporaryUploadingName));
                //if (onFailedUpload) onFailedUpload();
                // if we canceled we don't want to notify user
                if (response.code && response.code === "ERR_CANCELED") return;
                enqueueSnackbar(response, "error");
              });
            break;
          case UploadContext.RequestMetadata:
            ingestService
              .UploadAndReplaceRequestMetadata(objectId, fileData.file!, undefined, onUploadProgress)
              .then((response) => {
                if (!response.success) {
                  enqueueSnackbar("Oops! " + response.message, "error");
                  setUploadProgress({ percent: 0, size: "" });
                  updateStatus("readyToUpload");
                  return;
                }

                enqueueSnackbar(t("alert:success.requestMetadataFileUploaded"), "success");
                setFileData(undefined);
                updateStatus("noFile");
                onSuccessUpload && onSuccessUpload();
              });
            break;

          case UploadContext.ThumbnailBase64:
            const base64String = await readFileAsBinaryString(fileData.file);
            setUploadProgress({ percent: 100, size: "" });
            updateStatus("uploadDone");
            onSuccessUpload && onSuccessUpload(base64String);

            break;
          case UploadContext.PlatformResource:
            if (!resourceType) return;

            userService.EditPlatformIcon(fileData.file, resourceType).then((response) => {
              if (!response.success) {
                enqueueSnackbar("Oops! " + response.message, "error");
                setUploadProgress({ percent: 0, size: "" });
                updateStatus("readyToUpload");
                return;
              }

              enqueueSnackbar(t("alert:success.updateRessourceImage"), "success");
              setFileData(undefined);
              updateStatus("uploadDone");
              setTimeout(() => updateStatus("noFile"), 50);
              onSuccessUpload && onSuccessUpload();
            });
            break;
        }
      }, timeout);
    },
    [fileData, uploadStatus, objectId, format, abortController, enqueueSnackbar]
  );

  // on click on cancel button
  const onClickCancel = React.useCallback(() => {
    // we don't want to cancel when upload is done but waiting for server response so we return
    if (uploadProgress.percent >= 100) return;
    // abort call and instantiate new controller (previous one is aborted, can't change it)
    abortController.current.abort();
    abortController.current = new AbortController();
    // set upload progress and status
    setUploadProgress({ percent: 0, size: "" });
    updateStatus("readyToUpload");
  }, [uploadProgress, abortController]);

  // on click on delete button
  const onClickDelete = React.useCallback(() => {
    // if no file, return
    if (!alreadyUploadedFile && !fileData) return;
    // get the correct file id
    const fileId = fileData ? fileData.id : alreadyUploadedFile!.id;
    // we delete file in back
    mediaService.DeleteMediaFile(fileId).then((response) => {
      // we remove data from state and notify user of success or error
      if (response.success) {
        setFileData(undefined);
        updateStatus("noFile");
        enqueueSnackbar(t("alert:success.fileDeleted"), "success");
      } else {
        enqueueSnackbar(t("alert:error.fileDeleted", { message: response.message }), "error");
      }
    });
  }, [fileData, alreadyUploadedFile, enqueueSnackbar]);
  // #endregion

  // #region hooks
  // expose method for upload to parent
  React.useImperativeHandle(ref, () => ({
    performUpload: handleUpload,
    performRemove: onClickRemove,
  }));

  React.useEffect(() => {
    // if index is given
    if (cardIndex !== undefined) {
      // create new entry in store
      dispatch(
        CreateUploadCard({
          uploadCardIndex: cardIndex,
          uploadCardStatus: alreadyUploadedFile ? "uploadDone" : file ? "readyToUpload" : "noFile",
        })
      );
      // on component unmount, delete entry in store
      return () => {
        dispatch(DeleteUploadCardData(cardIndex));
      };
    }
  }, [cardIndex, alreadyUploadedFile]);
  // #endregion

  const cardContent = React.useMemo(
    () => (
      <CardContent small={small} onClick={(e) => e.stopPropagation()}>
        <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
          <Stack
            direction={small ? "row" : "column"}
            justifyContent={small ? "space-between" : "flex-start"}
            alignItems={small ? "center" : "flex-start"}
            spacing={2}
            flex={1}
          >
            <Stack>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="body1" component="span" fontWeight={500} mb="2px">
                  {title}
                </Typography>
                {uploadStatus === "readyToUpload" ? (
                  <Chip
                    icon={<CloudOffOutlinedIcon />}
                    label={t("chip:label.notUploaded")}
                    color="warning"
                    size="micro"
                    variant="outlined"
                  />
                ) : uploadStatus === "uploadInProgress" ? (
                  <Chip
                    icon={<CloudUploadOutlinedIcon />}
                    label={`${t("common:uploading")}...`}
                    color="primary"
                    size="micro"
                    variant="outlined"
                  />
                ) : (
                  uploadStatus === "uploadDone" && (
                    <Chip
                      icon={<CloudDoneOutlinedIcon />}
                      label={t("common:uploaded")}
                      color="success"
                      size="micro"
                      variant="outlined"
                    />
                  )
                )}
              </Stack>
              {uploadStatus === "noFile" || alreadyUploadedFile ? (
                <>
                  {alreadyUploadedFile ? (
                    <Typography variant="body2" component="span" color="text.tier">
                      {alreadyUploadedFile.name}
                    </Typography>
                  ) : (
                    <Stack>
                      <Typography variant="body2" component="span" color="text.tier">
                        {t("card:uploadCard.body.uploadFileTypes", {
                          fileTypes: format.acceptsFiles["application/octet-stream"].join(" "),
                        })}
                      </Typography>
                      {recommendedRatio && (
                        <Typography variant="body2" component="span" color="text.tier">
                          {t("card:uploadCard.body.uploadFileRatio", { ratio: recommendedRatio })}
                        </Typography>
                      )}
                      <Typography variant="body2" component="span" color="text.tier">
                        {t("card:uploadCard.body.uploadFileSize", { fileSize: format.maxFileSizeMb })}
                      </Typography>
                    </Stack>
                  )}
                  {fileErrors.length > 0 &&
                    fileErrors.map((error) => (
                      <Typography variant="body2" component="span" color="error.main">
                        {error}
                      </Typography>
                    ))}
                </>
              ) : uploadStatus === "uploadInProgress" ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LinearProgress variant="determinate" value={uploadProgress.percent} sx={{ width: 124 }} />
                  <Stack direction="row" alignItems="center">
                    <Typography color="primary.main">{uploadProgress.percent}% &nbsp;</Typography>
                    <Typography color="text.tier">
                      {" "}
                      - {uploadProgress.size}/{GetFileSize(fileData!.file.size)}{" "}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Typography variant="body2" component="span" color="text.tier">
                  {fullPath || fileData?.file.name}
                </Typography>
              )}
            </Stack>
            <Stack direction="row" spacing={small ? 0 : 2} mt={1.5}>
              {!disableButton && (
                <Stack direction="row" alignItems="center" justifyContent="flex-end">
                  {uploadStatus === "noFile" ? (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FileUploadOutlinedIcon />}
                      size="small"
                      onClick={(e) => open()}
                    >
                      {t("common:upload")}
                    </Button>
                  ) : uploadStatus === "readyToUpload" ? (
                    <>
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<HighlightOffOutlinedIcon />}
                        onClick={onClickRemove}
                      >
                        {t("common:remove")}
                      </Button>
                      {uploadButtonInCard && (
                        <Button
                          size="small"
                          color="success"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => handleUpload()}
                          sx={{ ml: 1 }}
                        >
                          {t("common:upload")}
                        </Button>
                      )}
                    </>
                  ) : uploadStatus === "uploadInProgress" ? (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<HighlightOffOutlinedIcon />}
                      onClick={onClickCancel}
                    >
                      {t("common:cancel")}
                    </Button>
                  ) : (
                    uploadStatus === "uploadDone" &&
                    userHasRightToDelete && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        startIcon={<DeleteOutlineOutlinedIcon />}
                        onClick={onClickDelete}
                      >
                        {t("common:delete")}
                      </Button>
                    )
                  )}
                </Stack>
              )}
              {!small && uploadStatus !== "uploadInProgress" && (
                <>
                  {resolution && (
                    <Stack>
                      <Typography color="text.tier" variant="body2">
                        {alreadyUploadedFile ? t("common:resolution") : t("common:minResolution")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {resolution}
                      </Typography>
                    </Stack>
                  )}
                  {ratio && (
                    <Stack>
                      <Typography color="text.tier" variant="body2">
                        {t("common:ratio")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {ratio}
                      </Typography>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          </Stack>
        </Stack>
        {!small && (
          <Picture
            coverRounded={coverRounded}
            coverPlaceholderIcon={uploadStatus === "uploadInProgress" ? true : coverPlaceholderIcon}
            recommendedRatio={recommendedRatio}
          >
            {fileData && (fileData.file.type === "image/jpeg" || fileData.file.type === "image/png") && (
              <img src={URL.createObjectURL(fileData.file)} alt="" />
            )}
            {!fileData &&
              context === UploadContext.ProfilePicture &&
              user?.picture !== null &&
              user?.picture !== undefined && <img src={"data:image/jpeg;base64," + user?.picture.$value} alt="" />}
            {(!fileData || (fileData.file.type !== "image/jpeg" && fileData.file.type !== "image/png")) &&
              context !== UploadContext.ProfilePicture &&
              (uploadStatus === "uploadInProgress" ? <DownloadingOutlinedIcon /> : coverPlaceholderIcon)}
          </Picture>
        )}
      </CardContent>
    ),
    [
      // commented props are not needed cause already in callbacks, callbacks are in dependency list so if a commented prop change it will trigger content re-rendering
      // we keep props commented just to be aware of why we don't have it in DL
      title,
      resolution,
      ratio,
      // error,
      cover,
      coverRounded,
      coverPlaceholderIcon,
      small,
      alreadyUploadedFile,
      // formatData,
      // dropzoneOptions,
      // onDropAccepted,
      // onRemoveFileByFormat,
      // onCancel,
      userHasRightToDelete,
      // onDeleteByFormat,
      uploadStatus,
      fileErrors,
      fileData,
      uploadProgress,
      onDrop,
      onUploadProgress,
      onClickCancel,
      onClickRemove,
      onClickDelete,
      updateStatus,
      file,
    ]
  );

  if (!lockUpload)
    return (
      <StyledCard
        // isDragReject is set to false because we need mimeTypes of accepted files to display an error on dragging, we don't have those so it will set an error each time we drag something
        // see first state "isErrored" for further informations
        isDragReject={false /*isDragReject*/}
        error={isErrored /*error*/}
        active={isDragActive}
        uploadStatus={uploadStatus}
      >
        {uploadStatus !== "noFile" ? (
          <>{cardContent}</>
        ) : (
          <label htmlFor={name} {...getRootProps()}>
            <input name={name} id={name} {...getInputProps()} />
            <>{cardContent}</>
          </label>
        )}
      </StyledCard>
    );
  else
    return (
      <StyledCard
        // isDragReject is set to false because we need mimeTypes of accepted files to display an error on dragging, we don't have those so it will set an error each time we drag something
        // see first state "isErrored" for further informations
        isDragReject={false /*isDragReject*/}
        error={false}
        active={false}
        uploadStatus={"noFile"}
      >
        {cardContent}
      </StyledCard>
    );
});

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) =>
    prop !== "error" && prop !== "active" && prop !== "isDragReject" && prop !== "uploadStatus",
})<{
  error: boolean;
  isDragReject: boolean;
  active: boolean;
  uploadStatus: UploadCardStatus;
}>(({ theme, error, isDragReject, active, uploadStatus }) => ({
  label: {
    width: "100%",
    height: "100%",
  },
  border: `1px ${uploadStatus !== "noFile" ? "solid" : "dashed"} ${theme.palette.backgroundAlpha.border}`,
  transition: "background .3s",
  ...(uploadStatus === "noFile" && {
    label: {
      cursor: "pointer",
    },
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
  }),
  ...(active && {
    borderColor: theme.palette.backgroundAlpha.borderFocus,
    background: theme.palette.backgroundAlpha.borderFocus,
  }),
  ...((isDragReject || error) && {
    borderColor: theme.palette.error.main,
  }),
  ...(isDragReject && {
    background: theme.palette.error.light,
  }),
}));

const CardContent = styled("div", { shouldForwardProp: (prop) => prop !== "small" })<{ small?: boolean }>(
  ({ theme, small }) => ({
    display: "flex",
    alignItems: "center",
    padding: small
      ? `${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(1.5)} ${theme.spacing(1.5)}`
      : theme.spacing(2),
  })
);

const Picture = styled("figure", {
  shouldForwardProp: (prop) => prop !== "coverRounded" && prop !== "coverPlaceholderIcon",
})<{
  coverRounded?: UploadCardProps["coverRounded"];
  recommendedRatio?: "16 / 9" | "1 / 1" | "4 / 3";
  coverPlaceholderIcon?: UploadCardProps["coverPlaceholderIcon"];
}>(({ theme, recommendedRatio, coverRounded, coverPlaceholderIcon }) => ({
  height: 80,
  aspectRatio: recommendedRatio ?? "1 / 1",
  borderRadius: coverRounded ? 40 : 6,
  marginLeft: theme.spacing(2),
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundImage: coverPlaceholderIcon
    ? "none"
    : "radial-gradient(102.68% 102.68% at 102.68% 0%, rgba(51, 104, 244, 0.8) 0%, rgba(51, 104, 244, 0.3) 53.4%, rgba(51, 104, 244, 0.3) 100%)",
  backgroundColor: coverPlaceholderIcon ? theme.palette.backgroundAlpha.main : "transparent",
  img: {
    ...theme.helpers(theme).fullImage,
  },
  ".MuiSvgIcon-root": {
    fontSize: 40,
    color: theme.palette.text.tier,
  },
}));

export default UploadCard;
